/**
 * OnboardingGuard — route-level guard used by Chat, Voice Talk and Life
 * Report. If the user isn't signed in → redirect to the landing / sign-in.
 * If they ARE signed in but any of the 5 mandatory fields are missing on
 * their profile, send them to /onboarding/your-beginning so they can finish
 * before entering a feature that depends on their birth chart.
 *
 * Mandatory fields (match backend `require_onboarded_user`):
 *   date_of_birth · time_of_birth · birth_city (+coords) · gender · marital_status
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, useAuth } from "@/hooks/useAuth";

const REQUIRED = ["date_of_birth", "time_of_birth", "birth_city", "gender", "marital_status"];

export default function OnboardingGuard({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [checking, setChecking] = useState(true);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Not signed in → bounce to landing with signin modal
      nav("/?signin=1", { replace: true, state: { from: loc.pathname } });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/profile");
        const birth = data?.birth || {};
        const missing = REQUIRED.filter((k) => !birth[k]);
        if (birth.latitude == null || birth.longitude == null) {
          if (!missing.includes("birth_city")) missing.push("birth_city");
        }
        if (!alive) return;
        if (missing.length) {
          nav("/onboarding/your-beginning", {
            replace: true,
            state: { from: loc.pathname, missing },
          });
        } else {
          setOk(true);
        }
      } catch (e) {
        // On any read error, defer to onboarding — safer than letting a
        // half-filled profile hit the chat engine.
        if (alive) nav("/onboarding/your-beginning", { replace: true });
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [user, loading, nav, loc.pathname]);

  if (loading || checking || !ok) {
    return (
      <div style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#FAFAFC",
      }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#5D00FF" }} />
      </div>
    );
  }
  return children;
}
